// Which funnel stage the ad page opens on (#6612). Imported campaigns sync in "angle" mode, so their
// stages are named angle_1, angle_2 and so on, never the three native funnel stages. The page's tab
// state used to start on a native stage regardless, and every headline and description consumer
// filters on `ad_funnel_stage.funnel === activeFunnelTab`, so an imported ad matched nothing and
// rendered empty over copy that was fully present. Pure function, shared by the ad page.
import { FunnelStage } from '@/lib/types';
import { getFunnelStageOrder } from '@/lib/utils';
import type { AdFunnelStage } from '@/lib/types';

/**
 * The stages the ad page offers, in the order it shows them.
 *
 * Mirrors the Angles list: paused stages are left out, and the rest sort by funnel order, which
 * leaves angle names in their existing relative order because they all sort equal.
 */
export function visibleFunnelStages(stages: AdFunnelStage[] | null | undefined): AdFunnelStage[] {
  return (stages ?? [])
    .filter((stage) => !stage.is_paused)
    .sort((a, b) => getFunnelStageOrder(a.funnel as string) - getFunnelStageOrder(b.funnel as string));
}

/**
 * The tab the ad page should be showing, given the ad's stages and the tab it is on.
 *
 * Keeps the current tab when that ad actually has it, so switching ads inside one campaign does not
 * throw away the user's choice. Otherwise falls to the ad's first visible stage, which is what makes
 * an imported ad open on Angle 1 instead of an empty Bottom of Funnel. Returns the current tab
 * unchanged when the ad has no stages at all, since the copy components then fall back to showing
 * the three native tabs and there is nothing better to point at.
 */
export function resolveActiveFunnelTab(
  stages: AdFunnelStage[] | null | undefined,
  activeTab: FunnelStage,
): FunnelStage {
  const visible = visibleFunnelStages(stages);
  if (visible.length === 0) return activeTab;
  if (visible.some((stage) => stage.funnel === activeTab)) return activeTab;
  return visible[0].funnel as FunnelStage;
}
